import React from 'react';
import ContrastTag from '../../../../../components/shared/ContrastTag';
import { DualShape } from '../../../../../types/geneCodes/AiPanelReportTypes';
import { getSeverityColor } from './severity';
import { DualShapeList, RecordCard, tags, text } from './primitives';

// ⚠️ ONE RENDERER, NOT NINE. `AvoidList`, `BiohackingList`, `ClinicalConsiderations`,
// `FactGroups`, `FurtherReadingList`, `PharmaceuticalList`, `RecommendationList`,
// `SportsSections` and `StrengthsList` were the SAME component — a `DualShapeList` of
// `RecordCard(title, extra?, rows[])` — differing only in which field carried the title,
// which carried the badge, and the label strings. Those three things are now data, in
// `sections.tsx`, and this is the only place that renders them.

// A field is either a scalar row or a list. The legacy rendered list-valued fields as chips
// (`gr-food-tag`) and bullet lists, never as a comma-joined sentence; at 400px the difference
// between "MTHFR, MTRR, COMT" and three chips is most of the readability.
export type RecordField<T> =
	| [label: string, get: (entry: T) => React.ReactNode]
	| [label: string, get: (entry: T) => unknown, kind: 'tags'];

export interface RecordSpec<T> {
	title: (entry: T) => string | null;
	tag?: (entry: T) => string | null;
	fields: Array<RecordField<T>>;
}

export const SeverityTag: React.FC<{ value?: string | null }> = ({ value }) => {
	const label = text(value);
	if (!label) return null;
	return (
		<ContrastTag background={getSeverityColor(label)} style={{ fontSize: 'var(--mg-fs-xxs)' }}>
			{label.toUpperCase()}
		</ContrastTag>
	);
};

export function RecordList<T>({ items, spec }: {
	items?: Array<DualShape<T>>;
	spec: RecordSpec<T>;
}): React.ReactElement | null {
	return (
		<DualShapeList<T>
			items={items}
			render={(entry) => (
				<RecordCard
					title={spec.title(entry) ?? 'Untitled'}
					extra={spec.tag ? <SeverityTag value={spec.tag(entry)} /> : null}
					rows={spec.fields.map((field) =>
						field[2] === 'tags'
							? { label: field[0], value: tags(field[1](entry)) }
							: { label: field[0], value: (field[1] as (e: T) => React.ReactNode)(entry) },
					)}
				/>
			)}
		/>
	);
}
